import cx from "classnames";
import { HTMLProps, MouseEvent as ReactMouseEvent, ReactNode, RefObject, useMemo } from "react";

import ButtonLink from "./ButtonLink";

import "./Button.scss";

type ButtonVariant = "primary" | "primary-action" | "secondary" | "tertiary" | "link" | "ghost";
type ButtonSize = "small" | "medium" | "large";

type ButtonProps = Omit<HTMLProps<HTMLButtonElement>, "size"> & {
  children: ReactNode;
  variant: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  textAlign?: "center" | "left" | "right";
  disabled?: boolean;
  onClick?: (event: ReactMouseEvent) => void;
  to?: string;
  type?: "button" | "submit" | "reset";
  imgSrc?: string;
  imgAlt?: string;
  imgClassName?: string;
  newTab?: boolean;
  showExternalLinkArrow?: boolean;
  buttonRef?: RefObject<HTMLButtonElement>;
  startIcon?: ({ ...props }: React.SVGProps<SVGSVGElement>) => JSX.Element;
  endIcon?: ({ ...props }: React.SVGProps<SVGSVGElement>) => JSX.Element;
  slim?: boolean;
  qa?: string;
};

export default function Button({
  variant,
  size = "medium",
  disabled,
  onClick,
  children,
  textAlign = "center",
  to,
  className,
  imgSrc,
  imgAlt = "",
  imgClassName = "",
  type,
  newTab,
  buttonRef,
  showExternalLinkArrow: showExternalLinkArrowOverride,
  slim,
  qa,
  startIcon,
  endIcon,
  ...rest
}: ButtonProps) {
  const classNames = cx("button", variant, className, textAlign, size, { slim });
  const showExternalLinkArrow = showExternalLinkArrowOverride ?? variant === "secondary";

  const img = useMemo(() => {
    if (!imgSrc) {
      return null;
    }

    return <img className={cx("btn-image", imgClassName)} src={imgSrc} alt={imgAlt} />;
  }, [imgSrc, imgAlt, imgClassName]);

  function handleClick(event: ReactMouseEvent) {
    if (disabled || !onClick) {
      return;
    }

    if (onClick) {
      onClick(event);
    }
  }

  if (to) {
    return (
      <ButtonLink
        className={classNames}
        to={to}
        onClick={onClick}
        newTab={newTab}
        showExternalLinkArrow={showExternalLinkArrow}
        disabled={disabled}
        ref={buttonRef}
        qa={qa}
        {...rest}
      >
        {startIcon &&
          startIcon({
            className: "btn-icon start-icon",
          })}
        {img}
        {children}
        {endIcon &&
          endIcon({
            className: "btn-icon end-icon",
          })}
      </ButtonLink>
    );
  }

  if (onClick) {
    return (
      <button
        data-qa={qa}
        ref={buttonRef}
        type={type}
        className={classNames}
        onClick={handleClick}
        disabled={disabled}
        {...rest}
      >
        {startIcon &&
          startIcon({
            className: "btn-icon start-icon",
          })}
        {img}
        {children}
        {endIcon &&
          endIcon({
            className: "btn-icon end-icon",
          })}
      </button>
    );
  }

  return (
    <button data-qa={qa} ref={buttonRef} type={type} className={classNames} disabled={disabled} {...rest}>
      {startIcon &&
        startIcon({
          className: "btn-icon start-icon",
        })}
      {img}
      {children}
      {endIcon &&
        endIcon({
          className: "btn-icon end-icon",
        })}
    </button>
  );
}
